import React, { useState } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/api/client"
import { InventoryBatch, ProductResponseDto } from "@/api/api-client"
import { format } from "date-fns"
import { Plus, Package, Calendar, X } from "lucide-react"
import { toastSuccess } from "@/lib/toast"
import { ScrollArea } from "@/components/ui/scroll-area"
import { EmptyState } from "@/components/ui/empty-state"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/hooks/useLanguage"
import { useWarehouses } from "@/hooks/useWarehouses"
import { WarehouseDisplay } from "@/components/warehouse/WarehouseDisplay"

interface BatchManagementSheetProps {
  product: ProductResponseDto | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const BatchManagementSheet: React.FC<BatchManagementSheetProps> = ({
  product,
  open,
  onOpenChange,
}) => {
  const queryClient = useQueryClient()
  const { t, language } = useLanguage()
  const text = t.batchManagement
  const [isAdding, setIsAdding] = useState(false)
  const [newBatch, setNewBatch] = useState({
    batchNumber: "",
    quantity: 0,
    expirationDate: "",
    unitCost: product?.unitCost || 0,
    warehouseId: undefined as number | undefined,
  })

  const { data: warehouses } = useWarehouses()

  React.useEffect(() => {
    if (product) {
      setNewBatch((prev) => ({ ...prev, unitCost: product.unitCost }))
    }
  }, [product])

  const { data: batches, isLoading } = useQuery({
    queryKey: ["product-batches", product?.id],
    queryFn: () => apiClient.getProductBatches(product!.id),
    enabled: !!product && open,
  })

  const createBatchMutation = useMutation({
    mutationFn: (data: any) => apiClient.createInventoryBatch(product!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-batches", product?.id] })
      queryClient.invalidateQueries({ queryKey: ["vendor-products"] })
      setIsAdding(false)
      setNewBatch({
        batchNumber: "",
        quantity: 0,
        expirationDate: "",
        unitCost: product?.unitCost || 0,
        warehouseId: undefined,
      })
      toastSuccess(text.success)
    },
  })

  const handleAddBatch = (e: React.FormEvent) => {
    e.preventDefault()
    createBatchMutation.mutate({
      batchNumber: newBatch.batchNumber,
      quantity: newBatch.quantity,
      expirationDate: newBatch.expirationDate
        ? new Date(newBatch.expirationDate).toISOString()
        : undefined,
      unitCost: newBatch.unitCost,
      arrivalDate: new Date().toISOString(),
      warehouseId: newBatch.warehouseId,
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full flex-col overflow-hidden border-l border-border bg-muted p-0 shadow-2xl sm:max-w-[500px]">
        <div className="flex h-full flex-col px-1">
          <SheetHeader className="border-b bg-background p-6">
            <div className="flex flex-col gap-1">
              <SheetTitle className="font-display text-2xl tracking-widest">{text.title}</SheetTitle>
              {product && (
                <p className="text-xs font-bold uppercase tracking-widest text-accent">
                  {product.name}
                </p>
              )}
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="space-y-8 p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg tracking-wider text-muted-foreground">
                  {text.active}
                </h3>
                <Button
                  size="sm"
                  onClick={() => setIsAdding(!isAdding)}
                  variant={isAdding ? "ghost" : "outline"}
                  className="h-8 rounded-lg border-2 border-accent/20 text-xs font-bold uppercase tracking-widest hover:border-accent"
                >
                  {isAdding ? <X className="mr-1 h-3 w-3" /> : <Plus className="mr-1 h-3 w-3" />}
                  {isAdding ? text.cancel : text.add}
                </Button>
              </div>

              {isAdding && (
                <form
                  onSubmit={handleAddBatch}
                  className="space-y-4 rounded-lg border-2 border-accent/10 bg-accent/5 p-4 animate-in fade-in slide-in-from-top-2"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-widest">
                        {text.batchNo}
                      </Label>
                      <Input
                        required
                        value={newBatch.batchNumber}
                        onChange={(e) => setNewBatch({ ...newBatch, batchNumber: e.target.value })}
                        className="h-10 rounded-lg border-input bg-background text-xs shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-widest">
                        {text.qty}
                      </Label>
                      <Input
                        required
                        type="number"
                        value={newBatch.quantity}
                        onChange={(e) =>
                          setNewBatch({ ...newBatch, quantity: parseFloat(e.target.value) })
                        }
                        className="h-10 rounded-lg border-input bg-background text-xs shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-widest">
                        {text.exp}
                      </Label>
                      <Input
                        type="date"
                        value={newBatch.expirationDate}
                        onChange={(e) =>
                          setNewBatch({ ...newBatch, expirationDate: e.target.value })
                        }
                        className="h-10 rounded-lg border-input bg-background text-xs shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-widest">
                        {text.cost}
                      </Label>
                      <Input
                        required
                        type="number"
                        step="0.01"
                        value={newBatch.unitCost}
                        onChange={(e) =>
                          setNewBatch({ ...newBatch, unitCost: parseFloat(e.target.value) })
                        }
                        className="h-10 rounded-lg border-input bg-background text-xs shadow-sm"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-widest">
                        {text.warehouse}
                      </Label>
                      <select
                        value={newBatch.warehouseId ?? ""}
                        onChange={(e) =>
                          setNewBatch({
                            ...newBatch,
                            warehouseId: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className="h-10 w-full appearance-none rounded-lg border border-input bg-background px-3 text-xs font-medium text-foreground shadow-sm focus:border-primary focus:outline-none"
                      >
                        <option value="">— {text.noWarehouse} —</option>
                        {warehouses?.map((w) => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="h-12 w-full rounded-lg bg-primary font-display text-sm tracking-label shadow-md hover:opacity-90"
                    disabled={createBatchMutation.isPending}
                  >
                    {createBatchMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin border-2 border-current border-t-transparent" />
                        <span>{text.creating}</span>
                      </div>
                    ) : (
                      text.save
                    )}
                  </Button>
                </form>
              )}

              <div className="space-y-3">
                {isLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
                    ))}
                  </div>
                ) : batches && batches.length > 0 ? (
                  batches.map((batch: InventoryBatch) => {
                    const batchWarehouse = batch.warehouse ?? (
                      batch.warehouseId
                        ? warehouses?.find((w) => w.id === batch.warehouseId)
                        : null
                    )
                    return (
                    <div
                      key={batch.id}
                      className={cn(
                        "flex items-start justify-between gap-4 rounded-lg border-2 bg-white p-4 transition-all duration-300",
                        batch.quantity <= 0
                          ? "border-border opacity-60"
                          : "border-border shadow-sm hover:border-accent"
                      )}
                    >
                      <div className="flex min-w-0 flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "font-display text-lg tracking-widest",
                              batch.quantity <= 0 ? "text-muted-foreground" : "text-foreground"
                            )}
                          >
                            {batch.batchNumber}
                          </span>
                          {batch.quantity <= 0 && (
                            <span className="border border-destructive/20 bg-destructive/5 px-2 py-0.5 text-xs font-bold uppercase tracking-label text-destructive">
                              {text.outOfStock}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Package className="h-3 w-3" /> {batch.quantity} /{" "}
                            {batch.initialQuantity}
                          </span>
                          {batch.expirationDate && (
                            <span className="flex items-center gap-1.5">
                              <Calendar className="h-3 w-3" />{" "}
                              {format(new Date(batch.expirationDate), "dd/MM/yyyy")}
                            </span>
                          )}
                        </div>
                        {batchWarehouse && (
                          <WarehouseDisplay
                            warehouse={batchWarehouse}
                            fallbackName={`Almacén #${batch.warehouseId}`}
                            className="mt-1 max-w-full"
                          />
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <p
                          className={cn(
                            "text-sm font-black tabular-nums",
                            batch.quantity <= 0 ? "text-muted-foreground" : "text-accent"
                          )}
                        >
                          ${batch.unitCost}
                        </p>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          {format(new Date(batch.arrivalDate), "dd/MM/yy")}
                        </p>
                      </div>
                    </div>
                    )
                  })
                ) : (
                  <EmptyState
                    icon={Package}
                    message={text.empty}
                    className="rounded-lg bg-muted/10 py-20"
                  />
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  )
}

